import { ref, getCurrentInstance } from 'vue'
import store from '@/store'
import { useToast } from '@/composables/common/useToast'

function defaultPayloadBuilder(asientos, { concepto, etiqueta }) {
    const params = {
        filters: {},
        lista_id: store.getters.getListaId,
        recinto_id: store.getters.getRecinto.id,
        butacas: asientos.value
    }

    if (concepto && concepto.length > 0) {
        params.concepto = concepto
    }
    if (etiqueta) {
        params.etiqueta_bloqueo_id = etiqueta.id
    }

    return params
}

export function useBlockSeats({
    endpoint = 'back.abonos.bloquear.butacas',
    payloadBuilder = defaultPayloadBuilder
} = {}) {
    const bloqueando = ref(false)
    const concepto = ref('')
    const etiqueta = ref(null)

    const instance = getCurrentInstance()
    const config = instance?.proxy?.config ?? store.getters.getConfig
    const toast = useToast()

    function bloquearAsientos(asientos, opts = {}, refreshCallback) {
        if (typeof opts === 'function') {
            refreshCallback = opts
            opts = {}
        }

        if (opts.concepto !== undefined) {
            concepto.value = opts.concepto
        }
        if (opts.etiqueta !== undefined) {
            etiqueta.value = opts.etiqueta
        }

        bloqueando.value = true

        const ctx = {
            concepto: concepto.value,
            etiqueta: etiqueta.value,
            n_butacas: opts.n_butacas,
            zona_id: opts.zona_id
        }

        const resolvedEndpoint = typeof endpoint === 'function' ? endpoint(config) : endpoint
        const params = payloadBuilder(asientos, ctx)

        // Devuelve la promesa del dispatch para que el llamante pueda esperar el
        // resultado real de la operación en lugar de inferirlo de la UI. Sin
        // esto, un consumidor sólo puede observar el efecto (el modal cerrando,
        // el grid recargando) y no sabe si la petición se resolvió o falló.
        //
        // El error NO se re-lanza: el composable ya lo notifica con un toast y
        // los consumidores actuales no capturan la promesa, así que re-lanzarlo
        // produciría un unhandled rejection. La promesa resuelve al resultado del
        // dispatch en éxito y a `null` en fallo, que es lo que permite ramificar.
        return store.dispatch(config.getVuexAlias(resolvedEndpoint), params).then(res => {
            concepto.value = ''
            etiqueta.value = null
            if (refreshCallback) refreshCallback()
            return res
        }).catch(error => {
            console.error('Error al bloquear:', error)
            toast.error(error?.response?.data?.message || 'Error al bloquear butacas')
            return null
        }).finally(() => {
            bloqueando.value = false
        })
    }

    return {
        // `loading` es el contrato de carga que el resto de composables expone;
        // `bloqueando` se mantiene como alias para no romper a los consumidores
        // que ya lo usan con ese nombre.
        loading: bloqueando,
        bloqueando,
        concepto,
        etiqueta,
        bloquearAsientos
    }
}
